// src/components/Sidebar/Sidebar.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiGrid, FiFolder } from "react-icons/fi";
import "./Sidebar.scss";

import CreateProjectModal from "../../Pages/CreateProject/CreateProject";
import { useAuth } from "../../contexts/AuthContext";
import SidebarHeader from "./SidebarHeader";
import SidebarSection from "./SidebarSection";
import SidebarProjectList from "./SidebarProjectList";
import SidebarFooter from "./SidebarFooter";
import SidebarQuickAdd from "./SidebarQuickAdd";
import { useProjects } from "./useProjects";
import { useProjectStatusMap } from "./useProjectStatusMap";

const COLLAPSE_STORAGE_KEY = "sidebar:collapsed";
const MOBILE_BREAKPOINT = 768;
const EXPANDED_WIDTH = "248px";
const COLLAPSED_WIDTH = "72px";

function readStoredCollapsed() {
  try {
    return window.localStorage.getItem(COLLAPSE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export default function Sidebar() {
  const { projects, loading, refresh } = useProjects();
  const statusMap = useProjectStatusMap(projects);
  const { user } = useAuth();
  const workspaceName = user?.companyName?.trim() || "Workspace";

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [collapsed, setCollapsed] = useState(readStoredCollapsed);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT,
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  const asideRef = useRef(null);
  const mobileTriggerRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  // Track viewport so the same component can render as a fixed rail on
  // desktop or an off-canvas drawer on mobile without duplicating markup.
  useEffect(() => {
    const onResize = () => {
      const nowMobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(nowMobile);
      if (!nowMobile) setMobileOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Let the rest of the app (e.g. a navbar hamburger button) open the
  // drawer without needing a prop/context wire-up:
  // window.dispatchEvent(new CustomEvent("sidebar-toggle"))
  useEffect(() => {
    const onExternalToggle = () => setMobileOpen((v) => !v);
    window.addEventListener("sidebar-toggle", onExternalToggle);
    return () => window.removeEventListener("sidebar-toggle", onExternalToggle);
  }, []);

  // App.scss's `.main-content` / `.content-with-sidebar` read the global
  // `--sidebar-width` variable to know how much space to reserve. Keep it
  // in sync here so main-content actually expands when the rail
  // collapses, and reserves zero space while the sidebar is an
  // off-canvas drawer on mobile (it isn't part of the layout flow then).
  useEffect(() => {
    const root = document.documentElement;
    if (isMobile) {
      root.style.setProperty("--sidebar-width", "0px");
    } else {
      root.style.setProperty(
        "--sidebar-width",
        collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
      );
    }
  }, [collapsed, isMobile]);

  const toggleCollapse = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(COLLAPSE_STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* ignore persistence failures (private mode, quota, etc.) */
      }
      return next;
    });
  }, []);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
    mobileTriggerRef.current?.focus?.();
  }, []);

  // ESC closes the mobile drawer; Tab is trapped inside it while open.
  useEffect(() => {
    if (!isMobile || !mobileOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        closeMobile();
        return;
      }

      if (e.key === "Tab" && asideRef.current) {
        const focusable = asideRef.current.querySelectorAll(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown);
    asideRef.current?.querySelector("button, a, [tabindex]")?.focus?.();

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isMobile, mobileOpen, closeMobile]);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (!(isMobile && mobileOpen)) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [isMobile, mobileOpen]);

  const goDashboard = () => {
    navigate("/dashboard");
    if (isMobile) closeMobile();
  };

  // Unchanged: schedule a "seen" signal 3 minutes after opening a
  // project; badge clearing itself lives in NotificationCenter.
  const goProject = useCallback(
    (id) => {
      setTimeout(() => {
        try {
          window.dispatchEvent(
            new CustomEvent("project-opened-for-seen", {
              detail: { projectId: id },
            }),
          );
        } catch {
          /* ignore */
        }
      }, 180000);

      navigate(`/project/${id}?view=table`);
      if (isMobile) closeMobile();
    },
    [navigate, isMobile, closeMobile],
  );

  const activeProjectId = useMemo(() => {
    const match = location.pathname.match(/\/project\/([^/?]+)/);
    return match ? match[1] : null;
  }, [location.pathname]);

  const isDashboardActive = location.pathname === "/dashboard";

  const asideClassName = [
    "Sidebar-sidebar-full",
    collapsed && !isMobile ? "is-collapsed" : "",
    isMobile ? "is-mobile" : "",
    isMobile && mobileOpen ? "is-open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      {isMobile && (
        <button
          ref={mobileTriggerRef}
          type="button"
          className="Sidebar-mobile-trigger"
          onClick={() => setMobileOpen(true)}
          aria-label="Open sidebar"
          aria-expanded={mobileOpen}
        >
          <FiGrid />
        </button>
      )}

      {isMobile && mobileOpen && (
        <div
          className="Sidebar-overlay"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}

      <aside
        ref={asideRef}
        className={asideClassName}
        aria-label="Sidebar"
        aria-hidden={isMobile && !mobileOpen}
      >
        <SidebarHeader
          collapsed={collapsed}
          onToggleCollapse={toggleCollapse}
          isMobile={isMobile}
          onCloseMobile={closeMobile}
          workspaceName={workspaceName}
        />

        {/* Scrollable nav region: everything between header and footer
            flexes to fill remaining height, and ONLY the project list
            inside it scrolls (see Sidebar.scss for the flex chain). */}
        <nav className="Sidebar-nav" aria-label="Primary">
          <button
            type="button"
            className={`Sidebar-dashboard-btn ${isDashboardActive ? "active" : ""}`}
            onClick={goDashboard}
            title="Dashboard"
          >
            <FiGrid className="Sidebar-icon" />
            {!collapsed && <span className="Sidebar-label">Dashboard</span>}
          </button>

          <SidebarSection
            icon={FiFolder}
            label="All Projects"
            collapsed={collapsed}
            grow
          >
            <SidebarProjectList
              projects={projects}
              loading={loading}
              activeProjectId={activeProjectId}
              statusMap={statusMap}
              collapsed={collapsed}
              onSelect={goProject}
            />
          </SidebarSection>
        </nav>

        <SidebarQuickAdd
          projects={projects}
          activeProjectId={activeProjectId}
          collapsed={collapsed}
          onOpen={() => isMobile && closeMobile()}
        />

        <SidebarFooter
          collapsed={collapsed}
          onCreateProject={() => setShowCreateModal(true)}
        />
      </aside>

      {showCreateModal && (
        <CreateProjectModal
          onClose={() => {
            setShowCreateModal(false);
            setTimeout(() => {
              refresh({ silent: true }).catch(() => {});
            }, 120);
          }}
        />
      )}
    </>
  );
}
